import { range, sort } from "@prismicio/editor-support/Array"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Avatar,
  Box,
  Button,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ErrorBoundary,
  Skeleton,
  Text
} from "@prismicio/editor-ui"
import { Suspense, useMemo, useState } from "react"

import {
  type UploaderWithMeta,
  useUploaders
} from "../../../hooks/uploadersData"
import { useMediaLibrary } from "../../MediaLibraryContext"

export function UploaderFilter() {
  const { uploaderId, onFilterChange } = useMediaLibrary()

  const [filterSearchTerm, setFilterSearchTerm] = useState("")
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [optimisticUploader, setOptimisticUploader] =
    useState<UploaderWithMeta>()

  useOnChange(uploaderId, () => {
    if (!uploaderId) {
      setOptimisticUploader(undefined)
    }
  })

  function onOpenChange(newOpen: boolean) {
    setDropdownOpen(newOpen)
    if (!newOpen) setFilterSearchTerm("")
  }

  function onUploaderToggle(newUploader?: UploaderWithMeta) {
    setOptimisticUploader(newUploader)
    onFilterChange({ uploaderId: newUploader?.id })
    setDropdownOpen(false)
  }

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger>
        <Button
          startIcon="people"
          renderStartIcon={(icon) =>
            optimisticUploader ? (
              <Avatar
                imageUrl={optimisticUploader?.uploadedAvatar}
                firstName={optimisticUploader.firstName}
                lastName={optimisticUploader.lastName}
                size={16}
                hasBorder={false}
              />
            ) : (
              icon
            )
          }
          endIcon="arrowDropDown"
          size="small"
          color="grey"
        >
          {optimisticUploader?.name ?? "All uploaders"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        minWidth={256}
        align="start"
        border={false}
        childrenFocusScope
      >
        <ComboBox variant="attached">
          <ComboBoxInput
            value={filterSearchTerm}
            onValueChange={setFilterSearchTerm}
            placeholder="Search for uploaders"
            // The combobox requires the input to be focused in order for
            // it to be keyboard navigateable.
            // setting autofocus means on open of the dropdown menu, we immediately
            // focus into the combobox so that users can keyboard navigate
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <ComboBoxContent paddingBlock={4}>
            <ErrorBoundary
              renderError={() => (
                <Text
                  color="grey9"
                  variant="small"
                  sx={{ marginBlock: 8, marginInline: 8 }}
                >
                  Error getting uploaders
                </Text>
              )}
            >
              <Suspense fallback={<UploaderListFallback />}>
                <UploaderList
                  searchTerm={filterSearchTerm}
                  setSearchTerm={setFilterSearchTerm}
                  onUploaderToggle={onUploaderToggle}
                  optimisticUploader={optimisticUploader}
                />
              </Suspense>
            </ErrorBoundary>
          </ComboBoxContent>
        </ComboBox>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface UploaderListProps {
  onUploaderToggle: (newUploader?: UploaderWithMeta) => void
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  optimisticUploader?: UploaderWithMeta
}

function UploaderList(props: UploaderListProps) {
  const { searchTerm, optimisticUploader, onUploaderToggle } = props

  const { uploaders } = useUploaders()

  const filteredUploaders = useMemo(() => {
    const searchTermLowerCase = searchTerm.toLowerCase()
    return sort(
      uploaders.filter(
        (uploader) =>
          uploader.name.toLowerCase().includes(searchTermLowerCase) ||
          uploader.email?.toLowerCase().includes(searchTermLowerCase)
      ),
      // Currently selected uploader should always be at the top
      (uploader) => (optimisticUploader?.id === uploader?.id ? 0 : 1)
    )
  }, [uploaders, optimisticUploader, searchTerm])

  if (!filteredUploaders.length)
    return (
      <Text
        color="grey9"
        variant="small"
        sx={{ marginBlock: 8, marginInline: 8 }}
      >
        No uploaders found
      </Text>
    )

  return (
    <>
      {filteredUploaders.map((uploader) => {
        const checked = uploader.id === optimisticUploader?.id
        return (
          <ComboBoxItem
            key={uploader.id}
            value={uploader.id}
            onCheckedChange={() =>
              checked ? onUploaderToggle() : onUploaderToggle(uploader)
            }
            checked={checked}
          >
            <Box
              alignItems="center"
              padding={{ top: 8, bottom: 8, left: 16, right: 16 }}
              gap={8}
            >
              <Avatar
                firstName={uploader.firstName}
                lastName={uploader.lastName}
                imageUrl={uploader.uploadedAvatar}
                hasBorder={false}
              />
              <Box display="flex" flexDirection="column">
                <Text color="grey12" variant="normal">
                  {uploader.name}
                </Text>
                {uploader.email && (
                  <Text color="grey10" variant="small">
                    {uploader.email}
                  </Text>
                )}
              </Box>
            </Box>
          </ComboBoxItem>
        )
      })}
    </>
  )
}

function UploaderListFallback() {
  return (
    <Box padding={8} flexDirection="column" gap={8}>
      {range(1, 4).map((num) => (
        <Skeleton key={num} height={48} width="full" />
      ))}
    </Box>
  )
}
